// stores/message/useMessageStore.ts

import { defineStore } from 'pinia'
import { createIndexedDBStore } from '@/stores/_base/createIndexedDBStore'
import { createMessageService } from '@/services/message/messageService'
import type { Message, MessageThread } from '@farmfree/common/types/message/message'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import type { Timestamp } from '@farmfree/common/firebase/firebaseTypes'
import { Timestamp as FirestoreTimestamp } from 'firebase/firestore'
import { useAuthStore } from '@/stores/auth/useAuthStore'
import { useContactStore } from '@/stores/contact/useContactStore'
import { computed, ref } from 'vue'

/**
 * Timestamp를 밀리초 숫자로 변환
 * @param time - Timestamp
 * @returns 밀리초 숫자
 */
const toMilliseconds = (time: Timestamp | undefined): number => {
  if (!time) return 0
  return time.toMillis()
}

// createIndexedDBStore 팩토리를 사용하여 기본 Store 생성
const baseMessageStore = createIndexedDBStore<Message>({
  storeId: 'message',
  tableName: 'messages',
  versionField: 'messageVersion',
  service: {
    getModified: async (companyId: string, since: number): Promise<ApiResponse<Message[]>> => {
      const messageService = createMessageService()
      const authStore = useAuthStore()
      const adminUid = authStore.currentUser?.uid

      if (!adminUid) {
        return {
          isSuccess: false,
          message: '관리자 UID가 없습니다.',
          data: [],
        }
      }

      // 메시지 로드 (since 파라미터 전달)
      const result = await messageService.getAllSmsMessagesWithCache(adminUid, since)

      if (!result.isSuccess || !result.data) {
        return {
          isSuccess: false,
          message: result.message || '메시지 로드 실패',
          data: [],
        }
      }

      // getAllSmsMessagesWithCache는 전체 메시지를 반환
      // syncWithServer가 병합 처리를 담당하므로 여기서는 필터링하지 않음
      return {
        isSuccess: true,
        data: result.data, // Message[] 전체 반환
      }
    },

    getDeleted: async (companyId: string): Promise<ApiResponse<string[]>> => {
      // 메시지는 삭제되지 않음
      return {
        isSuccess: true,
        data: [],
      }
    },
  },
  options: {
    debug: false,
  },
})

// 확장된 messageStore 생성
export const useMessageStore = defineStore('messageExtended', () => {
  const store = baseMessageStore()

  // 추가 상태
  const currentThread = ref<MessageThread | null>(null)

  // 메시지 관련 computed
  const allMessages = computed<Message[]>(() => {
    return store.items // store.items = Message[] 직접 사용
  })

  // threads를 messages에서 자동 생성하는 헬퍼 함수
  const generateThreadsFromMessages = (messages: Message[]): MessageThread[] => {
    const threadMap = new Map<string, Message[]>()

    // 전화번호별로 그룹화
    messages.forEach((msg) => {
      if (!threadMap.has(msg.address)) {
        threadMap.set(msg.address, [])
      }
      threadMap.get(msg.address)!.push(msg)
    })

    // MessageThread 객체 생성
    const contactStore = useContactStore()
    const authStore = useAuthStore()
    const companyId = authStore.currentCompany?.id || ''

    return Array.from(threadMap.entries()).map(([phoneNumber, msgs]) => {
      const sorted = msgs.sort((a, b) => {
        const timeA = toMilliseconds(a.dateMessageCreated)
        const timeB = toMilliseconds(b.dateMessageCreated)
        return timeA - timeB
      })

      const lastMsg = sorted[sorted.length - 1]
      const contact = contactStore.getContactByPhone(phoneNumber)

      const now = FirestoreTimestamp.now()

      return {
        id: phoneNumber,
        companyId,
        phoneNumber,
        contactName: contact?.name || '',
        lastMessage: lastMsg?.messageBody || '',
        lastMessageTime: lastMsg?.dateMessageCreated || now,
        unreadCount: 0,
        messageCount: msgs.length,
        dateCreated: now,
        dateModified: now,
      }
    })
  }

  const allThreads = computed<MessageThread[]>(() => {
    return generateThreadsFromMessages(allMessages.value)
  })

  const sortedThreads = computed<MessageThread[]>(() => {
    return [...allThreads.value].sort((a: MessageThread, b: MessageThread) => {
      const timeA = toMilliseconds(a.lastMessageTime)
      const timeB = toMilliseconds(b.lastMessageTime)
      return timeB - timeA
    })
  })

  // 메서드
  const getMessage = (messageId: string): Message | undefined => {
    return allMessages.value.find((msg) => msg.id === messageId)
  }

  const getConversation = (phoneNumber: string): Message[] => {
    return allMessages.value
      .filter((msg) => msg.address === phoneNumber)
      .sort((a, b) => {
        const timeA = toMilliseconds(a.dateMessageCreated)
        const timeB = toMilliseconds(b.dateMessageCreated)
        return timeA - timeB
      })
  }

  const getThread = (phoneNumber: string): MessageThread | undefined => {
    return allThreads.value.find((thread) => thread.phoneNumber === phoneNumber)
  }

  const selectThread = (threadId: string): void => {
    const thread: MessageThread | undefined = allThreads.value.find(
      (t: MessageThread) => t.id === threadId
    )
    currentThread.value = thread || null
  }

  const clearError = (): void => {
    store.error = null
  }

  const clearMessageCache = (): void => {
    store.items = []
  }

  const reset = (): void => {
    store.$reset()
    currentThread.value = null
  }

  const isThreadRecentlyUpdated = (threadId: string): boolean => {
    const thread: MessageThread | undefined = allThreads.value.find(
      (t: MessageThread) => t.id === threadId
    )
    if (!thread) return false

    const now = Date.now()
    const threadTime = toMilliseconds(thread.lastMessageTime)
    const fiveMinutes = 5 * 60 * 1000
    return now - threadTime < fiveMinutes
  }

  return {
    ...store,
    // 추가 상태
    currentThread,
    isLoading: store.loading,
    // computed
    allMessages,
    allThreads,
    sortedThreads,
    // 메서드
    getMessage,
    getConversation,
    getThread,
    selectThread,
    clearError,
    clearMessageCache,
    reset,
    isThreadRecentlyUpdated,
  }
})
